import React, { useContext, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Grid, Container, Button } from '@material-ui/core';
import { useConfirm } from 'hooks';
import i18n from 'i18n-js';
import Logs from 'components/Logs';
import { usePrevious } from 'hooks';
import Title from 'components/Title';
import { UserContext } from 'context';
import Layout from 'components/Layout';
import useStyles from './Details.styled';
import Comments from 'components/Comments';
import { useParams, useHistory } from 'react-router-dom';
import { Details } from 'components/VehiclesGroups';
import DocumentsWidget from 'components/Documents/Widget';
import { BusinessHoursWidget } from 'components/BusinessHours';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import Can from 'components/Can';
import { FETCH_STATE } from 'constants';
import {
  RulesetsWidget,
  VehicleGroupsLabel,
  VehicleGroupImages,
  VehicleGroupLocations,
  VehicleGroupsPricingRules,
  VehicleGroupServicesWidget,
} from 'components/VehiclesGroups';
import { buildUrl } from 'utils';

const VehiclesGroupsDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const classes = useStyles();
  const VGLabel = usePrevious(VehicleGroupsLabel());
  const { orgType } = useContext(UserContext);
  const { current } = useSelector((state) => state.vehiclesGroups);
  const { core } = useSelector((state) => state.config?.settings || {});
  const hasPricingRulesSetEnabled = core?.pricing?.pricing_rules_set || false;
  const vehicleGroupId = parseInt(id);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const fetchDeleteVehicleGroup = useSelector(
    (state) => state.vehiclesGroups.fetchDeleteVehicleGroup
  );

  const deleteLoading = fetchDeleteVehicleGroup === FETCH_STATE.LOADING;

  if (isNaN(vehicleGroupId)) {
    history.push(buildUrl('/service-groups'));
    return <></>;
  }

  const [toggleDeleteVehicleGroup, DeleteVehicleGroupConfirm] = useConfirm({
    title: i18n.t('vehicle_groups.delete_vehicle_group'),
    message: i18n.t('vehicle_groups.delete_vehicle_group_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: useCallback(
      () => dispatch(VehiclesGroupsActions.deleteVehiceGroup(id, history)),
      [dispatch, id]
    ),
  });

  return (
    <Layout
      title={i18n.t('vehicle_groups.details', {
        type: VGLabel,
      })}
    >
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container justifyContent="space-between" style={{ marginBottom: 20 }}>
              <Grid>
                <Title
                  title={`${i18n.t('vehicle_groups.details', {
                    type: VGLabel,
                  })}${current?.name ? ' - ' + current.name : ''}`}
                />
              </Grid>
              <Grid item>
                <Can I="delete" a="vehicle_group">
                  <Box display="flex" flexWrap="wrap">
                    <Button
                      color="primary"
                      variant="contained"
                      data-cy="delete-service-group"
                      className={classes.actionBtn}
                      onClick={toggleDeleteVehicleGroup}
                      disabled={deleteLoading}
                    >
                      {i18n.t('vehicle_groups.delete_vehicle_group')}
                    </Button>
                  </Box>
                </Can>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item sm={12} md={6} lg={4}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Details id={id} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <VehicleGroupImages id={vehicleGroupId} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item sm={12} md={6} lg={4}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <VehicleGroupLocations id={vehicleGroupId} />
                  </Grid>
                  {orgType === 'msOwnerOrg' && (
                    <Grid item xs={12}>
                      <VehicleGroupServicesWidget id={vehicleGroupId} />
                    </Grid>
                  )}
                  {hasPricingRulesSetEnabled && (
                    <Grid item xs={12}>
                      <VehicleGroupsPricingRules id={vehicleGroupId} />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <DocumentsWidget id={vehicleGroupId} type="vehicle_group" />
                  </Grid>
                  <Grid item xs={12}>
                    <Comments id={vehicleGroupId} type="vehicle_group" />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item sm={12} md={6} lg={4}>
                <Grid container spacing={2}>
                  {orgType !== 'msOwnerOrg' && !core?.subscription?.enabled && (
                    <Grid item xs={12}>
                      <BusinessHoursWidget id={parseInt(id)} />
                    </Grid>
                  )}
                  {current?.group_type && (
                    <Grid item xs={12} sm={12}>
                      <Logs
                        title={i18n.t('vehicle_groups.logs', {
                          type: VGLabel,
                        })}
                        targetType={
                          current?.group_type === 'CUSTOMER'
                            ? 'CustomerVehicleGroup'
                            : 'OwnerVehicleGroup'
                        }
                        targetId={vehicleGroupId}
                        height={400}
                      />
                    </Grid>
                  )}
                  {orgType !== 'msOwnerOrg' &&
                    !core?.subscription?.enabled &&
                    sharingType !== 'FFB' && (
                      <Grid item xs={12}>
                        <RulesetsWidget id={vehicleGroupId} />
                      </Grid>
                    )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
      <DeleteVehicleGroupConfirm />
    </Layout>
  );
};

export default VehiclesGroupsDetails;
